/**
 * Cyberware Section — shared render module for the Phase 3 body grid
 * + Phase 4 cyberware install loop (#589).
 *
 * Mounted by both `runner-sidebar.ts` (playback / forensic view, usually
 * display-only) and `runners.ts` (active-roster detail panel, which
 * supplies the INSTALL_CYBERWARE submit handler so the install action
 * is available in live planning).
 *
 * Usage:
 *
 *   import {
 *     renderCyberwareUI, wireCyberwareUI, injectCyberwareStyles,
 *   } from './cyberware-section';
 *
 *   injectCyberwareStyles();              // once per process; idempotent
 *   container.innerHTML += renderCyberwareUI({ runner, ripperdocs, cleanCash, onInstallCyberware });
 *   wireCyberwareUI(container, { runner, ripperdocs, cleanCash, onInstallCyberware });
 *
 * `renderCyberwareUI` emits HTML; `wireCyberwareUI` attaches event
 * handlers (toggle, select cascades, submit). The split lets consumers
 * batch multiple sections into one innerHTML write and wire them all
 * after a single DOM update.
 */

import type {
  RunnerDTO, RipperdocDTO, PartDTO,
  CyberwareInstalledDTO, CyberwareModsDTO, RipperdocInventoryEntryDTO,
} from '../data/fixture';
import { t } from '../i18n';

// ── Public API ───────────────────────────────────────────────────────────

export interface CyberwareUIOptions {
  runner: RunnerDTO;
  /** Discovered ripperdocs. Empty / undefined → "scout sectors first"
   *  empty state when an install callback is supplied. */
  ripperdocs?: RipperdocDTO[];
  /** Owner's current clean cash. Drives the affordability gate in the
   *  cost preview. Undefined leaves affordability unchecked (server
   *  validator still rejects on submit). */
  cleanCash?: number;
  /** Optional install handler. Undefined → display-only mode; the
   *  install form is hidden entirely. Resolves to `true` on success,
   *  `false` on validator/server failure (an error string can also
   *  be thrown for inline display). */
  onInstallCyberware?: (
    runnerId: string,
    ripperdocNodeId: string,
    cyberwareId: string,
    targetPart: string,
  ) => Promise<boolean>;
  /** Optional REMOVE_CYBERWARE handler (Phase 4b, server v1.85.0+).
   *  Undefined → REMOVE buttons hidden on installed cyberware slots.
   *  Resolves to true on submit success, false on failure. The first
   *  discovered alive ripperdoc is the implicit removal destination
   *  for the trimmed UI; future phases may add a doc picker. */
  onRemoveCyberware?: (
    runnerId: string,
    ripperdocNodeId: string,
    targetPart: string,
  ) => Promise<boolean>;
}

/** Emit the section HTML. Always returns at minimum an empty string;
 *  body grid is omitted when the runner has no `body` field (rival
 *  view / pre-Phase-3 fixture). Cyberware section is always present
 *  so the section header gives the player a stable target. */
export function renderCyberwareUI(opts: CyberwareUIOptions): string {
  return renderBodySection(opts) + renderHumanitySection(opts) + renderCyberwareSection(opts);
}

/** Wire interactive controls inside `root`. Idempotent — call once
 *  per render. Looks up form elements by id (#cw-toggle, #cw-rd-select,
 *  …); silently returns if not present. */
export function wireCyberwareUI(root: HTMLElement, opts: CyberwareUIOptions): void {
  const toggle = root.querySelector<HTMLButtonElement>('#cw-toggle');
  const form = root.querySelector<HTMLDivElement>('#cw-form');
  const rdSel = root.querySelector<HTMLSelectElement>('#cw-rd-select');
  const itemSel = root.querySelector<HTMLSelectElement>('#cw-item-select');
  const partSel = root.querySelector<HTMLSelectElement>('#cw-part-select');
  const submit = root.querySelector<HTMLButtonElement>('#cw-submit');
  if (!toggle || !form || !rdSel || !itemSel || !partSel || !submit) return;

  toggle.addEventListener('click', () => {
    const open = form.style.display !== 'none';
    form.style.display = open ? 'none' : 'flex';
    toggle.setAttribute('aria-expanded', String(!open));
  });

  const findRipperdoc = (): RipperdocDTO | undefined =>
    opts.ripperdocs?.find(r => r.id === rdSel.value);

  const findItem = (): RipperdocInventoryEntryDTO | undefined =>
    findRipperdoc()?.inventory?.find(it => it.cyberwareId === itemSel.value);

  const refreshItems = (): void => {
    const rd = findRipperdoc();
    const items = rd?.inventory ?? [];
    itemSel.innerHTML = items.length === 0
      ? `<option value="">${escapeHTML(t('sidebar.cyberware_no_stock'))}</option>`
      : items.map(it => {
          // Phase 4d — Tier-3 items get a lock badge in the option label
          // when the player rep is below 70. Final gate decision happens
          // in refreshPreview (sets submit.disabled), but surfacing the
          // lock at pick-time saves the player a wasted click.
          const tier3Lock = it.tier >= 3 && (rd?.reputation ?? 50) < 70
            ? ` 🔒`
            : '';
          return `<option value="${escapeAttr(it.cyberwareId)}">${escapeHTML(it.name)} · T${it.tier}${tier3Lock} · \$${it.baseCost}</option>`;
        }).join('');
    refreshParts();
  };

  const refreshParts = (): void => {
    const item = findItem();
    const parts = item?.targetParts ?? [];
    partSel.innerHTML = parts.length === 0
      ? `<option value="">—</option>`
      : parts.map(p => `<option value="${escapeAttr(p)}">${escapeHTML(partLabel(p))}</option>`).join('');
    refreshPreview();
  };

  const refreshPreview = (): void => {
    const preview = root.querySelector<HTMLDivElement>('#cw-preview');
    const err = root.querySelector<HTMLDivElement>('#cw-error');
    if (!preview || !err) return;
    err.style.display = 'none';
    const rd = findRipperdoc();
    const item = findItem();
    if (!rd || !item) {
      preview.innerHTML = '';
      submit.disabled = true;
      return;
    }
    // Phase 4b §21.4 / §21.5 — apply reputation multipliers so the
    // preview shows the same effective values the server will use.
    // Phase 4d §27 — Tier-3 rep-70 gate + ChromeWerks ×0.9 stack.
    const rep = rd.reputation ?? 50;
    const successBase = Math.max(0, (rd.surgerySkill / 100) * (1 - item.tier * 0.05));
    const repMult = 1 + (rep - 50) / 500;
    const pSuccess = Math.max(0, successBase * repMult);
    const successPct = Math.round(pSuccess * 100);
    const successCls = successPct >= 70 ? 'ok' : successPct >= 50 ? 'warn' : 'bad';
    const priceMult = 1.25 - rep / 200;
    const chromeWerksMult = rd.faction === 'chromewerks' ? 0.9 : 1.0;
    const effectivePrice = Math.round(item.baseCost * priceMult * chromeWerksMult);
    const cleanCash = opts.cleanCash;
    const canAfford = cleanCash == null || cleanCash >= effectivePrice;
    const costCls = canAfford ? '' : 'bad';
    const discountShown = effectivePrice !== item.baseCost;
    const costLabel = discountShown
      ? `${escapeHTML(t('sidebar.cyberware_effective_price'))} \$${effectivePrice.toLocaleString()} <span style="color:var(--text-lo,#64748b);text-decoration:line-through">\$${item.baseCost.toLocaleString()}</span>`
      : `${escapeHTML(t('sidebar.cyberware_cost'))} \$${item.baseCost.toLocaleString()}`;
    const hu = item.humanityCost ?? 0;
    // Phase 4d Tier-3 rep gate — locks the install if catalog tier is 3
    // and player rep is below 70. Server validator + op both also
    // check; the client surface is informational.
    const tier3Locked = item.tier >= 3 && rep < 70;
    const chromeWerksLabel = rd.faction === 'chromewerks'
      ? `<span class="ok">${escapeHTML(t('sidebar.cyberware_chromewerks_discount'))} -10%</span>`
      : '';
    const lockLabel = tier3Locked
      ? `<span class="bad">${escapeHTML(t('sidebar.cyberware_tier3_locked'))}</span>`
      : '';
    preview.innerHTML = `
      <span class="${costCls}">${costLabel}</span>
      <span class="${successCls}">${escapeHTML(t('sidebar.cyberware_success'))} ${successPct}%</span>
      ${hu > 0 ? `<span>${escapeHTML(t('sidebar.cyberware_humanity'))} -${hu}</span>` : ''}
      ${chromeWerksLabel}
      ${lockLabel}
    `;
    submit.disabled = !canAfford || !partSel.value || tier3Locked;
  };

  rdSel.addEventListener('change', refreshItems);
  itemSel.addEventListener('change', refreshParts);
  partSel.addEventListener('change', refreshPreview);

  submit.addEventListener('click', async () => {
    const rd = findRipperdoc();
    const item = findItem();
    const part = partSel.value;
    const err = root.querySelector<HTMLDivElement>('#cw-error');
    if (!rd || !item || !part || !opts.onInstallCyberware || !err) return;
    submit.disabled = true;
    submit.textContent = t('sidebar.cyberware_submitting');
    try {
      const ok = await opts.onInstallCyberware(opts.runner.id, rd.nodeId, item.cyberwareId, part);
      if (!ok) {
        err.textContent = t('sidebar.cyberware_submit_error');
        err.style.display = 'block';
      } else {
        // Collapse the form so the caller's fixture refresh re-renders
        // the new installed-slot row.
        form.style.display = 'none';
        toggle.setAttribute('aria-expanded', 'false');
      }
    } catch (e) {
      err.textContent = (e instanceof Error ? e.message : t('sidebar.cyberware_submit_error'));
      err.style.display = 'block';
    } finally {
      submit.textContent = t('sidebar.cyberware_submit');
      submit.disabled = false;
    }
  });

  // Initial population.
  refreshItems();

  // Phase 4b — wire REMOVE buttons on installed slots. Picks the first
  // alive ripperdoc the player has discovered as the implicit destination;
  // future Phase 4c may surface a doc picker for black-market routing.
  if (opts.onRemoveCyberware) {
    const removeDestination = (opts.ripperdocs ?? []).find(r => r.id)?.nodeId;
    root.querySelectorAll<HTMLButtonElement>('.cw-remove-btn').forEach(btn => {
      btn.addEventListener('click', async () => {
        const targetPart = btn.dataset.cwRemovePart;
        if (!targetPart || !removeDestination) return;
        if (!confirmRemove()) return;
        btn.disabled = true;
        const originalText = btn.textContent ?? '';
        btn.textContent = t('sidebar.cyberware_remove_submitting');
        try {
          const ok = await opts.onRemoveCyberware!(opts.runner.id, removeDestination, targetPart);
          if (!ok) {
            btn.textContent = t('sidebar.cyberware_remove_error');
            return;
          }
          // Success — leave button disabled; caller's fixture refresh
          // re-renders the slot panel (the now-permanent part will
          // show under the body grid).
        } catch (e) {
          btn.textContent = e instanceof Error ? e.message : t('sidebar.cyberware_remove_error');
        } finally {
          btn.disabled = false;
          if (btn.textContent === t('sidebar.cyberware_remove_submitting')) {
            btn.textContent = originalText;
          }
        }
      });
    });
  }
}

/** Native confirm() in production, override-able in tests via the
 *  window.__cwConfirm hook so jsdom doesn't choke on the modal. */
function confirmRemove(): boolean {
  const w = window as unknown as { __cwConfirm?: (msg: string) => boolean };
  const msg = t('sidebar.cyberware_remove_confirm') + '\n\n' + t('sidebar.cyberware_remove_warn');
  if (typeof w.__cwConfirm === 'function') return w.__cwConfirm(msg);
  if (typeof window.confirm === 'function') return window.confirm(msg);
  return true; // test-env fallback — proceed
}

/** Inject the section's CSS. Idempotent — second call is a no-op
 *  thanks to the `#cyberware-section-style` id check. */
export function injectCyberwareStyles(): void {
  if (document.querySelector('#cyberware-section-style')) return;
  const s = document.createElement('style');
  s.id = 'cyberware-section-style';
  s.textContent = CSS;
  document.head.appendChild(s);
}

// ── Private rendering ────────────────────────────────────────────────────

function renderBodySection(opts: CyberwareUIOptions): string {
  const body = opts.runner.body;
  if (!body) return '';
  const cyberware = opts.runner.cyberware ?? [];
  const cwByPart = new Map<string, CyberwareInstalledDTO>();
  for (const c of cyberware) cwByPart.set(c.part, c);
  const partOrder: Array<{ key: keyof typeof body; part: string; label: string }> = [
    { key: 'head',     part: 'head',      label: 'HEAD'  },
    { key: 'torso',    part: 'torso',     label: 'TORSO' },
    { key: 'leftArm',  part: 'arm_left',  label: 'L-ARM' },
    { key: 'rightArm', part: 'arm_right', label: 'R-ARM' },
    { key: 'leftLeg',  part: 'leg_left',  label: 'L-LEG' },
    { key: 'rightLeg', part: 'leg_right', label: 'R-LEG' },
  ];
  const cells = partOrder
    .map(({ key, part, label }) => renderBodyPart(label, body[key], cwByPart.get(part)))
    .join('');
  return `
    <div class="cw-section">
      <div class="cw-section-title">${escapeHTML(t('sidebar.body'))}</div>
      <div class="cw-body-grid">${cells}</div>
    </div>
  `;
}

function renderBodyPart(label: string, p: PartDTO, cw: CyberwareInstalledDTO | undefined): string {
  const cls = p.permanent && !cw ? 'cw-body-part cw-body-part--permanent' : 'cw-body-part';
  const overlay = (() => {
    if (cw) return `<span class="cw-body-part-cyber">◆ CHROME</span>`;
    if (p.permanent) {
      const since = p.permanentDay ? ` D${p.permanentDay}` : '';
      return `<span class="cw-body-part-wrecked">✕ ${escapeHTML(t('sidebar.body_wrecked'))}${since}</span>`;
    }
    return '';
  })();
  return `
    <div class="${cls}">
      <div class="cw-body-part-label">${label}</div>
      <div class="cw-body-part-hp">${p.hp}<span style="color:var(--text-lo)">/${p.maxHp}</span></div>
      ${overlay}
    </div>
  `;
}

// renderHumanitySection — Phase 5 humanity / cyberpsychosis surface
// (SPEC_combat.md §29-§33). Omitted entirely if the fixture predates
// v1.87.0 (no humanity field). The state badge + warning copy is the
// player-facing read-out for the LOY decay multiplier and Cyberpsycho
// friendly-fire / faction-leave behaviours that the engine applies on
// each end-of-day tick.
function renderHumanitySection(opts: CyberwareUIOptions): string {
  const r = opts.runner;
  if (r.humanity == null) return '';
  const hu = Math.max(0, Math.min(100, r.humanity));
  const state = r.humanityState ?? deriveHumanityState(hu);
  const cls = humanityBarCls(state);
  const stateLabel = humanityStateLabel(state);
  const warning = humanityWarning(state);
  return `
    <div class="cw-section">
      <div class="cw-section-title">${escapeHTML(t('sidebar.humanity'))}</div>
      <div class="cw-humanity-row">
        <div class="cw-humanity-bar">
          <div class="cw-humanity-bar-fill ${cls}" style="width:${hu}%"></div>
        </div>
        <div class="cw-humanity-value">${hu}<span style="color:var(--text-lo)">/100</span></div>
      </div>
      <div class="cw-humanity-meta">
        <span class="cw-humanity-badge ${cls}">${escapeHTML(stateLabel)}</span>
        ${warning}
      </div>
    </div>
  `;
}

// deriveHumanityState — fallback band lookup matching the engine
// (SPEC §30): Stable 100..80, Jittery 79..50, Unstable 49..25,
// Cyberpsycho 24..0. Used when the server didn't surface humanityState
// (older fixtures or test setups).
function deriveHumanityState(hu: number): 'stable' | 'jittery' | 'unstable' | 'cyberpsycho' {
  if (hu >= 80) return 'stable';
  if (hu >= 50) return 'jittery';
  if (hu >= 25) return 'unstable';
  return 'cyberpsycho';
}

function humanityStateLabel(state: string): string {
  switch (state) {
    case 'stable':      return t('sidebar.humanity_state_stable');
    case 'jittery':     return t('sidebar.humanity_state_jittery');
    case 'unstable':    return t('sidebar.humanity_state_unstable');
    case 'cyberpsycho': return t('sidebar.humanity_state_cyberpsycho');
    default:            return state;
  }
}

function humanityBarCls(state: string): string {
  switch (state) {
    case 'stable':      return 'cw-humanity--stable';
    case 'jittery':     return 'cw-humanity--jittery';
    case 'unstable':    return 'cw-humanity--unstable';
    case 'cyberpsycho': return 'cw-humanity--cyberpsycho';
    default:            return '';
  }
}

function humanityWarning(state: string): string {
  if (state === 'cyberpsycho') {
    return `<span class="cw-humanity-warn cw-humanity-warn--bad">${escapeHTML(t('sidebar.humanity_warning_cyberpsycho'))}</span>`;
  }
  if (state === 'unstable' || state === 'jittery') {
    return `<span class="cw-humanity-warn cw-humanity-warn--warn">${escapeHTML(t('sidebar.humanity_warning_low'))}</span>`;
  }
  return '';
}

function renderCyberwareSection(opts: CyberwareUIOptions): string {
  const r = opts.runner;
  const installed = r.cyberware ?? [];
  const ripperdocs = opts.ripperdocs ?? [];
  const canInstall = !!opts.onInstallCyberware
    && r.status !== 'arrested'
    && r.arrestedUntil != null && r.arrestedUntil < 0;

  const canRemove = !!opts.onRemoveCyberware
    && r.status !== 'arrested'
    && r.arrestedUntil != null && r.arrestedUntil < 0
    && ripperdocs.some(rd => true && rd); // any discovered doc is fine
  const installedHTML = installed.length === 0
    ? `<div class="cw-empty-inline">${escapeHTML(t('sidebar.cyberware_none'))}</div>`
    : installed.map(c => renderInstalledCyberware(c, canRemove)).join('');

  const formHTML = (() => {
    // No callback supplied → display-only. Skip the install form entirely;
    // installed slots stay visible above.
    if (!opts.onInstallCyberware) return '';
    if (!canInstall) {
      return `<div class="cw-empty">${escapeHTML(t('sidebar.cyberware_lock_arrested'))}</div>`;
    }
    if (ripperdocs.length === 0) {
      return `<div class="cw-empty">${escapeHTML(t('sidebar.cyberware_lock_no_ripperdoc'))}</div>`;
    }
    return renderInstallForm(ripperdocs);
  })();

  return `
    <div class="cw-section" id="cw-cyberware-section">
      <div class="cw-section-title">${escapeHTML(t('sidebar.cyberware'))} (${installed.length})</div>
      ${installedHTML}
      ${formHTML}
    </div>
  `;
}

function renderInstalledCyberware(c: CyberwareInstalledDTO, canRemove: boolean): string {
  const cls = c.partialFailure ? 'cw-slot cw-slot--partial' : 'cw-slot';
  const partial = c.partialFailure
    ? `<span class="cw-partial-badge">${escapeHTML(t('sidebar.cyberware_partial'))}</span>`
    : '';
  const mods = formatStatMods(c.statMods);
  const hu = (c.humanityCost ?? 0) > 0 ? ` · -${c.humanityCost}H` : '';
  const removeBtn = canRemove
    ? `<button class="cw-remove-btn" data-cw-remove-part="${escapeAttr(c.part)}" title="${escapeAttr(t('sidebar.cyberware_remove_warn'))}">${escapeHTML(t('sidebar.cyberware_remove_action'))}</button>`
    : '';
  return `
    <div class="${cls}">
      <div class="cw-slot-row">
        <span class="cw-slot-name">${escapeHTML(c.name)} ${partial}</span>
        ${removeBtn}
      </div>
      <div class="cw-slot-meta">
        <span>${escapeHTML(partLabel(c.part))} · T${c.tier}${hu}</span>
        ${mods}
      </div>
    </div>
  `;
}

function renderInstallForm(ripperdocs: RipperdocDTO[]): string {
  const optionsRD = ripperdocs.map(r => {
    const rep = r.reputation ?? 50;
    const repLabel = `${escapeHTML(t('sidebar.cyberware_rep'))} ${rep}`;
    const factionLabel = r.faction && r.faction !== 'independent'
      ? ` · ${escapeHTML(factionDisplayLabel(r.faction))}`
      : '';
    return `<option value="${escapeAttr(r.id)}" data-node="${escapeAttr(r.nodeId)}" data-rep="${rep}" data-faction="${escapeAttr(r.faction ?? 'independent')}">${escapeHTML(r.name)}${factionLabel} · ${escapeHTML(r.sectorId)} · ${escapeHTML(t('sidebar.cyberware_skill'))} ${r.surgerySkill} · ${repLabel}</option>`;
  }).join('');
  return `
    <button class="cw-install-toggle" id="cw-toggle" aria-expanded="false">
      ${escapeHTML(t('sidebar.cyberware_install_action'))}
    </button>
    <div class="cw-install-form" id="cw-form" style="display:none">
      <label>${escapeHTML(t('sidebar.cyberware_ripperdoc'))}
        <select id="cw-rd-select">${optionsRD}</select>
      </label>
      <label>${escapeHTML(t('sidebar.cyberware_item'))}
        <select id="cw-item-select"></select>
      </label>
      <label>${escapeHTML(t('sidebar.cyberware_part'))}
        <select id="cw-part-select"></select>
      </label>
      <div class="cw-cost-preview" id="cw-preview"></div>
      <div class="cw-error" id="cw-error" style="display:none"></div>
      <button class="cw-install-btn" id="cw-submit">${escapeHTML(t('sidebar.cyberware_submit'))}</button>
    </div>
  `;
}

// ── Module helpers ───────────────────────────────────────────────────────

// factionDisplayLabel translates the canonical lowercase faction enum
// into a localized display string. Falls back to the raw key when no
// translation is found (defense for unknown faction values from older
// fixtures or future-tier docs).
function factionDisplayLabel(faction: string): string {
  switch (faction) {
    case 'independent':
      return t('sidebar.cyberware_faction_independent');
    case 'cartel':
      return t('sidebar.cyberware_faction_cartel');
    case 'chromewerks':
      return t('sidebar.cyberware_faction_chromewerks');
    case 'arasaka_med':
      return t('sidebar.cyberware_faction_arasaka_med');
    default:
      return faction;
  }
}

function partLabel(part: string): string {
  switch (part) {
    case 'head':      return t('sidebar.body_head');
    case 'torso':     return t('sidebar.body_torso');
    case 'arm_left':  return t('sidebar.body_arm_left');
    case 'arm_right': return t('sidebar.body_arm_right');
    case 'leg_left':  return t('sidebar.body_leg_left');
    case 'leg_right': return t('sidebar.body_leg_right');
    default:          return part;
  }
}

function formatStatMods(mods: CyberwareModsDTO | undefined): string {
  if (!mods) return '';
  const parts: string[] = [];
  const push = (label: string, v: number | undefined) => {
    if (!v) return;
    const cls = v > 0 ? 'cw-stat-pos' : 'cw-stat-neg';
    const sign = v > 0 ? '+' : '';
    parts.push(`<span class="${cls}">${sign}${v} ${label}</span>`);
  };
  push('STR', mods.str);
  push('INT', mods.int);
  push('STL', mods.stl);
  push('TCH', mods.tch);
  push('CHA', mods.cha);
  push('CON', mods.con);
  return parts.join(' ');
}

function escapeHTML(s: string): string {
  return s.replace(/[&<>"']/g, c => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
  }[c] || c));
}

function escapeAttr(s: string): string {
  return escapeHTML(s);
}

// ── Styles ───────────────────────────────────────────────────────────────

const CSS = `
  .cw-section {
    background: var(--bg, #0a0e17);
    border: 1px solid var(--border, #1e293b);
    border-radius: 3px;
    padding: 10px;
    margin-top: 8px;
  }
  .cw-section-title {
    font: 700 12px var(--sans, system-ui);
    color: var(--text-hi, #f8fafc);
    margin-bottom: 8px;
  }

  .cw-empty-inline {
    color: var(--text-lo, #64748b);
    font: 11px var(--mono, monospace);
    padding: 4px 0;
  }
  .cw-empty {
    color: var(--text-lo, #64748b);
    font: 11px var(--mono, monospace);
    padding: 8px;
    border: 1px dashed var(--border, #1e293b);
    text-align: center;
    font-style: italic;
    margin-top: 6px;
  }

  .cw-body-grid {
    display: grid;
    grid-template-columns: 1fr 1fr 1fr;
    gap: 6px;
  }
  .cw-body-part {
    background: var(--bg-card, #111827);
    border: 1px solid var(--border, #1e293b);
    border-left: 3px solid var(--cyan-dim, #00f5d488);
    padding: 6px 8px;
    font: 11px var(--mono, monospace);
    position: relative;
  }
  .cw-body-part--permanent {
    border-left-color: var(--red, #e63946);
    background: rgba(230, 57, 70, 0.08);
  }
  .cw-body-part-label {
    color: var(--text-lo, #64748b);
    text-transform: uppercase;
    letter-spacing: 0.1em;
    font-size: 9px;
  }
  .cw-body-part-hp {
    color: var(--text-hi, #f8fafc);
    font-variant-numeric: tabular-nums;
    font-size: 12px;
  }
  .cw-body-part-wrecked {
    position: absolute;
    top: 4px;
    right: 6px;
    font-size: 8px;
    color: var(--red, #e63946);
    letter-spacing: 0.1em;
  }
  .cw-body-part-cyber {
    position: absolute;
    top: 4px;
    right: 6px;
    font-size: 8px;
    color: var(--cyan, #00f5d4);
    letter-spacing: 0.1em;
  }

  /* Phase 5 — humanity bar + state badge */
  .cw-humanity-row {
    display: flex;
    align-items: center;
    gap: 8px;
  }
  .cw-humanity-bar {
    flex: 1;
    height: 8px;
    background: var(--bg-card, #111827);
    border: 1px solid var(--border, #1e293b);
    border-radius: 2px;
    overflow: hidden;
  }
  .cw-humanity-bar-fill {
    height: 100%;
    transition: width 0.2s ease-out;
  }
  .cw-humanity-bar-fill.cw-humanity--stable      { background: var(--green, #06d6a0); }
  .cw-humanity-bar-fill.cw-humanity--jittery     { background: var(--amber, #fca311); }
  .cw-humanity-bar-fill.cw-humanity--unstable    { background: var(--orange, #f4845f); }
  .cw-humanity-bar-fill.cw-humanity--cyberpsycho { background: var(--red, #e63946); }
  .cw-humanity-value {
    font: 11px var(--mono, monospace);
    color: var(--text-hi, #f8fafc);
    font-variant-numeric: tabular-nums;
    min-width: 50px;
    text-align: right;
  }
  .cw-humanity-meta {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-top: 6px;
    flex-wrap: wrap;
  }
  .cw-humanity-badge {
    font: 9px var(--mono, monospace);
    padding: 2px 6px;
    border: 1px solid currentColor;
    text-transform: uppercase;
    letter-spacing: 0.1em;
  }
  .cw-humanity-badge.cw-humanity--stable      { color: var(--green, #06d6a0); }
  .cw-humanity-badge.cw-humanity--jittery     { color: var(--amber, #fca311); }
  .cw-humanity-badge.cw-humanity--unstable    { color: var(--orange, #f4845f); }
  .cw-humanity-badge.cw-humanity--cyberpsycho { color: var(--red, #e63946); }
  .cw-humanity-warn {
    font: 10px var(--mono, monospace);
    letter-spacing: 0.05em;
  }
  .cw-humanity-warn--warn { color: var(--amber, #fca311); }
  .cw-humanity-warn--bad  { color: var(--red, #e63946); }

  .cw-slot {
    background: var(--bg-card, #111827);
    border: 1px solid var(--border, #1e293b);
    border-left: 3px solid var(--cyan, #00f5d4);
    padding: 6px 8px;
    margin: 4px 0;
    font: 11px var(--mono, monospace);
    display: flex;
    flex-direction: column;
    gap: 2px;
  }
  .cw-slot--partial { border-left-color: var(--amber, #fca311); }
  .cw-slot-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
  }
  .cw-slot-name { color: var(--text-hi, #f8fafc); font-weight: 600; flex: 1; }
  .cw-remove-btn {
    background: transparent;
    border: 1px solid var(--red, #e63946);
    color: var(--red, #e63946);
    font: 9px var(--mono, monospace);
    padding: 2px 6px;
    cursor: pointer;
    text-transform: uppercase;
    letter-spacing: 0.08em;
  }
  .cw-remove-btn:hover:not(:disabled) { background: rgba(230, 57, 70, 0.1); }
  .cw-remove-btn:disabled {
    border-color: var(--text-lo, #64748b);
    color: var(--text-lo, #64748b);
    cursor: not-allowed;
  }
  .cw-slot-meta {
    color: var(--text-mid, #94a3b8);
    font-size: 10px;
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
  }
  .cw-stat-pos { color: var(--green, #06d6a0); }
  .cw-stat-neg { color: var(--red, #e63946); }
  .cw-partial-badge {
    display: inline-block;
    font-size: 9px;
    color: var(--amber, #fca311);
    border: 1px solid var(--amber, #fca311);
    padding: 1px 4px;
    letter-spacing: 0.1em;
  }

  .cw-install-toggle {
    background: transparent;
    border: 1px solid var(--cyan-dim, #00f5d488);
    color: var(--cyan, #00f5d4);
    font: 11px var(--mono, monospace);
    padding: 4px 8px;
    cursor: pointer;
    width: 100%;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    margin-top: 6px;
  }
  .cw-install-toggle:hover { border-color: var(--cyan, #00f5d4); }
  .cw-install-toggle:disabled {
    color: var(--text-lo, #64748b);
    border-color: var(--border, #1e293b);
    cursor: not-allowed;
  }
  .cw-install-form {
    background: var(--bg, #0a0e17);
    border: 1px solid var(--cyan-dim, #00f5d488);
    padding: 8px;
    margin-top: 8px;
    display: flex;
    flex-direction: column;
    gap: 6px;
  }
  .cw-install-form label {
    font: 9px var(--mono, monospace);
    color: var(--text-lo, #64748b);
    text-transform: uppercase;
    letter-spacing: 0.1em;
    display: flex;
    flex-direction: column;
    gap: 2px;
  }
  .cw-install-form select {
    font: 11px var(--mono, monospace);
    background: var(--bg-card, #111827);
    border: 1px solid var(--border, #1e293b);
    color: var(--text, #e0e8f0);
    padding: 3px 4px;
  }
  .cw-install-form select:focus {
    outline: 1px solid var(--cyan, #00f5d4);
    outline-offset: 1px;
  }
  .cw-cost-preview {
    display: flex;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 6px;
    color: var(--text-lo, #64748b);
    font: 10px var(--mono, monospace);
    padding: 4px 0;
  }
  .cw-cost-preview .ok { color: var(--green, #06d6a0); }
  .cw-cost-preview .warn { color: var(--amber, #fca311); }
  .cw-cost-preview .bad { color: var(--red, #e63946); }
  .cw-install-btn {
    background: transparent;
    border: 1px solid var(--amber, #fca311);
    color: var(--amber, #fca311);
    font: 11px var(--mono, monospace);
    padding: 5px 8px;
    cursor: pointer;
    text-transform: uppercase;
    letter-spacing: 0.1em;
  }
  .cw-install-btn:hover:not(:disabled) { background: rgba(252, 163, 17, 0.1); }
  .cw-install-btn:disabled {
    border-color: var(--text-lo, #64748b);
    color: var(--text-lo, #64748b);
    cursor: not-allowed;
  }
  .cw-error {
    color: var(--red, #e63946);
    font: 10px var(--mono, monospace);
    padding: 4px 0;
  }
`;
